/*
 * RIDECLOUD CONTACT SECTION
 * Style: Original RideCloud — white/light background, clean form, orange submit button
 * Layout: Two-column — Zoho form left, contact info panel right
 *
 * ZOHO INTEGRATION — METHOD 3 (server-side API):
 * - Form data posted via fetch() to /api/leads (Vercel serverless function)
 * - Server creates lead in Zoho CRM v7 API with Brand = RideCloud
 * - No page redirect — branded in-page thank-you shown on success
 */

import { useState } from "react";
import { MapPin, Phone, Mail, Clock, CheckCircle } from "lucide-react";

const contactInfo = [
  { icon: Phone, label: "Phone", value: "+27 83 656 5677", href: "tel:+27836565677" },
  { icon: Mail, label: "Email", value: "sales@ridecloud.co.za", href: "mailto:sales@ridecloud.co.za" },
  { icon: Clock, label: "Hours", value: "Mon–Fri 08:00–17:00 · Sat by appointment", href: null },
];

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.6rem 0.875rem",
  border: "1px solid rgba(46,0,79,0.2)",
  borderRadius: "0.375rem",
  fontSize: "0.875rem",
  color: "#2e004f",
  backgroundColor: "#ffffff",
  outline: "none",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
};
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.8rem",
  fontWeight: 600,
  color: "#2e004f",
  marginBottom: "0.375rem",
};

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  vehicle: string;
  description: string;
  consent: boolean;
  contactMethod: string;
}

const defaultForm: FormState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  vehicle: "",
  description: "",
  consent: false,
  contactMethod: "",
};

export default function ContactSection() {
  const [form, setForm] = useState<FormState>(defaultForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!form.firstName.trim()) { setError("First Name cannot be empty."); return; }
    if (!form.lastName.trim()) { setError("Last Name cannot be empty."); return; }
    if (!form.phone.trim()) { setError("Tel Number cannot be empty."); return; }
    if (!form.contactMethod) { setError("Please select how we should reach you."); return; }
    if (!form.consent) { setError("Please accept the consent checkbox."); return; }
    if (form.email.trim()) {
      const at = form.email.indexOf("@");
      const dot = form.email.lastIndexOf(".");
      if (at < 1 || dot < at + 2 || dot + 2 >= form.email.length) {
        setError("Please enter a valid email address."); return;
      }
    }

    setSubmitting(true);

    try {
      // POST to /api/leads — server handles both Zoho WebToLeadForm and portal logging
      fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formType: "Get In Touch",
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          contactMethod: form.contactMethod,
          vehicleOfInterest: form.vehicle.trim(),
          vehicleInMind: form.vehicle.trim(),
          description: form.description.trim(),
        }),
      }).catch(() => {});

      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20" style={{ backgroundColor: "#ffffff" }}>
      <div className="container">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: "#2e004f" }}>
            Get In Touch
          </h2>
          <p className="text-base" style={{ color: "#5a3d70" }}>
            Tell us which vehicle interests you and we'll get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Form panel */}
          <div
            className="lg:col-span-3 rounded-lg p-6 md:p-8"
            style={{ border: "1px solid rgba(46,0,79,0.12)", backgroundColor: "#f8f7fa" }}
          >
            {submitted ? (
              /* ── Thank-you state ── */
              <div className="flex flex-col items-center justify-center text-center py-10 gap-4">
                <CheckCircle className="w-14 h-14" style={{ color: "#e6531e" }} />
                <h3 className="text-2xl font-semibold" style={{ color: "#2e004f" }}>
                  Thank you, {form.firstName}!
                </h3>
                <p className="text-base max-w-sm" style={{ color: "#5a3d70" }}>
                  We've received your enquiry and a member of our team will be in touch within 24 hours.
                </p>
                {form.vehicle && (
                  <p className="text-sm font-medium px-4 py-2 rounded-full" style={{ backgroundColor: "rgba(230,83,30,0.08)", color: "#e6531e" }}>
                    Vehicle of interest: {form.vehicle}
                  </p>
                )}
                <button
                  onClick={() => { setForm(defaultForm); setSubmitted(false); }}
                  className="mt-2 text-sm underline"
                  style={{ color: "#9a8aaa" }}
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              /* ── Form state ── */
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {/* First + Last Name */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label style={labelStyle}>First Name <span style={{ color: "#e6531e" }}>*</span></label>
                    <input type="text" name="firstName" value={form.firstName} onChange={handleChange} maxLength={40} style={inputStyle} placeholder="First name" />
                  </div>
                  <div>
                    <label style={labelStyle}>Last Name <span style={{ color: "#e6531e" }}>*</span></label>
                    <input type="text" name="lastName" value={form.lastName} onChange={handleChange} maxLength={80} style={inputStyle} placeholder="Last name" />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label style={labelStyle}>Tel Number <span style={{ color: "#e6531e" }}>*</span></label>
                  <input type="text" name="phone" value={form.phone} onChange={handleChange} maxLength={30} style={inputStyle} placeholder="e.g. +27 82 234 4567" />
                </div>

                {/* Contact Method */}
                <div>
                  <label style={labelStyle}>How should we reach you? <span style={{ color: "#e6531e" }}>*</span></label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {(["Phone Call", "WhatsApp", "Email"] as const).map((opt) => (
                      <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, contactMethod: opt }))}
                        className="py-2 px-3 rounded-lg text-sm font-medium border transition-all"
                        style={{ backgroundColor: form.contactMethod === opt ? "#e6531e" : "#fff", color: form.contactMethod === opt ? "#fff" : "#2e004f", borderColor: form.contactMethod === opt ? "#e6531e" : "rgba(46,0,79,0.2)" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} maxLength={100} style={inputStyle} placeholder="your@email.com" />
                </div>

                {/* Preferred Vehicle */}
                <div>
                  <label style={labelStyle}>Preferred Vehicle</label>
                  <input type="text" name="vehicle" value={form.vehicle} onChange={handleChange} maxLength={255} style={inputStyle} placeholder="e.g. MG ZS, Chery Tiggo 7 Pro..." />
                </div>

                {/* Comments */}
                <div>
                  <label style={labelStyle}>Comments</label>
                  <textarea name="description" value={form.description} onChange={handleChange} rows={4} style={{ ...inputStyle, resize: "none" }} placeholder="Tell us about your needs, budget, or any questions..." />
                </div>

                {/* Consent */}
                <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: "#ffffff", borderColor: "rgba(46,0,79,0.1)" }}>
                  <input
                    type="checkbox"
                    name="consent"
                    id="consent_contact"
                    checked={form.consent}
                    onChange={handleChange}
                    className="mt-0.5 w-4 h-4 flex-shrink-0"
                    style={{ accentColor: "#e6531e" }}
                  />
                  <label htmlFor="consent_contact" className="text-sm leading-relaxed" style={{ color: "#4a3060" }}>
                    I agree to be contacted regarding this enquiry <span style={{ color: "#e6531e" }}>*</span>
                  </label>
                </div>

                {/* Error message */}
                {error && (
                  <p className="text-sm font-medium" style={{ color: "#c0392b" }}>{error}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-orange w-full justify-center"
                  style={{ opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? "Sending..." : "Send Enquiry"}
                </button>

                <p className="text-center text-xs" style={{ color: "#9a8aaa" }}>
                  By submitting, you agree to be contacted about your enquiry. We respect your privacy.
                </p>
              </form>
            )}
          </div>

          {/* Info panel */}
          <div className="lg:col-span-2 space-y-3">
            {contactInfo.map((item) => {
              const Icon = item.icon;
              const content = (
                <div
                  className="flex items-start gap-3 p-4 rounded-lg"
                  style={{ border: "1px solid rgba(46,0,79,0.1)", backgroundColor: "#f8f7fa" }}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "rgba(230,83,30,0.1)" }}
                  >
                    <Icon className="w-4 h-4" style={{ color: "#e6531e" }} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold mb-0.5" style={{ color: "#9a8aaa" }}>{item.label}</div>
                    <div className="text-sm font-medium" style={{ color: "#2e004f" }}>{item.value}</div>
                  </div>
                </div>
              );
              return item.href ? (
                <a key={item.label} href={item.href} className="block hover:opacity-80 transition-opacity">{content}</a>
              ) : (
                <div key={item.label}>{content}</div>
              );
            })}

            {/* Social links */}
            <div
              className="p-4 rounded-lg"
              style={{ border: "1px solid rgba(46,0,79,0.1)", backgroundColor: "#f8f7fa" }}
            >
              <p className="text-xs font-semibold mb-3" style={{ color: "#9a8aaa" }}>Follow Us</p>
              <div className="flex gap-2">
                {[
                  { label: "LinkedIn", icon: "in", href: "#" },
                  { label: "Twitter", icon: "𝕏", href: "#" },
                  { label: "Facebook", icon: "f", href: "#" },
                  { label: "Instagram", icon: "◉", href: "#" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                    style={{ border: "1px solid rgba(46,0,79,0.2)", color: "#2e004f", backgroundColor: "#ffffff" }}
                    title={s.label}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#2e004f"; e.currentTarget.style.color = "#ffffff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#ffffff"; e.currentTarget.style.color = "#2e004f"; }}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
